import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Ban } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

export default function CloverReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const result = params.get("result"); // success | cancel | failure
  const [order, setOrder] = useState(null);
  const [polls, setPolls] = useState(0);
  const reconcileTried = useRef(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get(`/clover/order/${orderId}`);
      setOrder(data);
      return data.status;
    } catch (e) {
      console.error("Clover order status poll failed:", e);
      return null;
    }
  }, [orderId]);

  // Fallback: if the webhook is delayed/missed, ask the backend to reconcile
  // this order against Clover's Payments API (server-side, verified).
  const reconcile = useCallback(async () => {
    if (reconcileTried.current) return;
    reconcileTried.current = true;
    try {
      await api.post(`/clover/reconcile/${orderId}`);
    } catch (e) {
      console.error("Clover reconcile fallback failed (will keep polling):", e);
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId || result !== "success") return;
    let active = true;
    (async () => {
      const status = await check();
      if (!active) return;
      if (status === "paid" || status === "declined") return;
      // After a few unsuccessful polls, trigger the server-side reconcile once.
      if (polls === 4) await reconcile();
      if (polls < 15) setTimeout(() => setPolls((p) => p + 1), 2000);
    })();
    return () => {
      active = false;
    };
  }, [orderId, result, polls, check, reconcile]);

  const status = order?.status;
  let view;
  if (result === "cancel") {
    view = { icon: Ban, color: "text-slate-400", title: "Checkout cancelled", msg: "You cancelled the Clover payment. No charge was made." };
  } else if (result === "failure") {
    view = { icon: XCircle, color: "text-rose-500", title: "Payment failed", msg: "The Clover payment did not go through. No order was completed." };
  } else if (status === "paid") {
    view = { icon: CheckCircle2, color: "text-emerald-500", title: "Payment confirmed!", msg: `Clover confirmed ${formatPrice(order?.amount_cents)}. Order ${order?.order_id} is paid.` };
  } else if (status === "declined") {
    view = { icon: XCircle, color: "text-rose-500", title: "Payment declined", msg: "Clover declined this payment. The order was not completed." };
  } else {
    view = { icon: Clock, color: "text-amber-500", title: "Confirming payment…", msg: "Waiting for Clover to confirm the payment. This updates automatically once the secure webhook arrives, and we double-check with Clover if it's delayed." };
  }
  const Icon = view.icon;

  return (
    <div className="max-w-md mx-auto text-center py-16" data-testid="clover-return-page">
      <Icon className={`mx-auto mb-4 ${view.color}`} size={56} />
      <h1 data-testid="clover-return-title" className="font-display font-bold text-3xl text-slate-800">
        {view.title}
      </h1>
      <p className="text-slate-500 mt-3">{view.msg}</p>
      {orderId && <p className="text-xs text-slate-400 mt-2">Order: {orderId}</p>}
      <button
        data-testid="clover-return-home"
        onClick={() => navigate("/orders")}
        className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl px-6 py-3"
      >
        View my orders
      </button>
    </div>
  );
}
